import React, { useState } from "react";
import {
  Editor,
  EditorProvider,
  Toolbar,
  BtnBold,
  BtnItalic,
  BtnUnderline,
  BtnStrikeThrough,
  BtnNumberedList,
  BtnBulletList,
  BtnLink,
  BtnClearFormatting,
  BtnUndo,
  BtnRedo,
  HtmlButton,
} from "react-simple-wysiwyg";

/**
 * ✅ Styles Dropdown Button
 */
function BtnStyles({ disabled = false }) {
  const applyStyle = (tag) => {
    if (!tag) return;

    try {
      document.execCommand("formatBlock", false, tag);
    } catch (e) {
      console.error("formatBlock failed:", e);
    }
  };

  return (
    <select
      disabled={disabled}
      defaultValue=""
      title="Styles"
      onChange={(e) => {
        applyStyle(e.target.value);
        e.target.value = "";
      }}
      className="se-styles"
    >
      <option value="" disabled>
        Styles
      </option>
      <option value="p">Normal</option>
      <option value="h1">Heading 1</option>
      <option value="h2">Heading 2</option>
      <option value="h3">Heading 3</option>
      <option value="h4">Heading 4</option>
      <option value="h5">Heading 5</option>
      <option value="h6">Heading 6</option>
      <option value="blockquote">Blockquote</option>
      <option value="pre">Code Block</option>
    </select>
  );
}

/**
 * ✅ Custom HTML button wrapper so we can track mode in our state
 */
function HtmlToggleButton({ disabled = false, onToggle }) {
  return (
    <span
      onMouseDown={() => {
        // HtmlButton toggles on click internally, so we mirror that toggle in our state
        if (!disabled) onToggle?.();
      }}
      style={{ display: "inline-flex" }}
    >
      <HtmlButton />
    </span>
  );
}

export default function SimpleEditor({
  value = "",
  onChange,

  // ✅ label + error
  label = "",
  required = false,
  error = "",

  // editor config
  placeholder = "Write here...",
  height = 220,
  disabled = false,

  // ✅ UI customization
  borderColor = "#dbe1ea",
  errorBorderColor = "#dc3545",
  toolbarHeight = 55,
}) {
  const [isHtmlMode, setIsHtmlMode] = useState(false);

  const handleChange = (e) => {
    onChange?.(e.target.value);
  };

  const finalBorderColor = error ? errorBorderColor : borderColor;

  return (
    <div className="simple-editor-wrapper">
      {/* Label */}
      {label ? (
		<label className={`${required ? "required" : ""} form-label`}>{label}{" "}</label>
      ) : null}

      {/* Editor Box */}
      <div
        className={`simple-editor-box ${error ? "has-error" : ""}`}
        style={{ borderColor: finalBorderColor }}
      >
        <EditorProvider>
          <Editor
            value={value || ""}
            onChange={handleChange}
            disabled={disabled}
            placeholder={placeholder}
            className="simple-editor"
            style={{ minHeight: height }}
          >
            <Toolbar
              className="se-toolbar"
              style={{ minHeight: toolbarHeight }}
            >
              {/* Default buttons */}
              <BtnBold />
              <BtnItalic />
              <BtnUnderline />
              <BtnStrikeThrough />
              <BtnNumberedList />
              <BtnBulletList />
              <BtnLink />
              <BtnClearFormatting />
              <BtnUndo />
              <BtnRedo />

              {/* ✅ HTML Toggle */}
              <HtmlToggleButton
                disabled={disabled}
                onToggle={() => setIsHtmlMode((prev) => !prev)}
              />

              {/* ✅ Styles dropdown only when NOT in HTML mode */}
              {!isHtmlMode ? <BtnStyles disabled={disabled} /> : null}
            </Toolbar>
          </Editor>
        </EditorProvider>
      </div>

      {/* Error */}
	  {error && <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">{error}</div>}
    </div>
  );
}
